import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Cpu, TrendingUp } from "lucide-react";
import { MaskText } from "./MaskText";
import { useLocale } from "@/i18n/LocaleContext";
import { SHARED_IMAGES } from "@/i18n/translations";

export const DualOffering = () => {
  const { t } = useLocale();
  const [hovered, setHovered] = useState(null);

  const panels = [
    { key: "solutions", Icon: Cpu, accent: "#00F0FF", img: SHARED_IMAGES.tech, href: "#services", data: t.dual.solutions },
    { key: "ventures", Icon: TrendingUp, accent: "#D4FF00", img: SHARED_IMAGES.team, href: "#ventures", data: t.dual.ventures },
  ];

  return (
    <section id="dual" className="relative border-t border-white/10" data-testid="dual-section">
      <div className="relative overflow-hidden border-b border-white/10 min-h-[440px] md:min-h-[520px] flex items-end" data-testid="dual-locale-bg">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <img src={t.about.localeImage} alt="" className="absolute inset-y-0 left-0 h-full w-full md:w-[62%] object-cover object-center grayscale" />
          <div className="absolute inset-0 bg-gradient-to-r from-nowi-bg/40 via-nowi-bg/70 to-nowi-bg md:from-transparent md:via-nowi-bg/60 md:to-nowi-bg" />
          <div className="absolute inset-0 bg-gradient-to-t from-nowi-bg/70 via-transparent to-transparent" />
        </div>

        <div className="absolute top-6 left-6 z-10 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.25em] text-nowi-text">
          <span className="w-6 h-px bg-nowi-venture" />
          {t.about.localeCaption}
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-28 md:pt-40 pb-14 w-full">
          <div className="md:w-1/2 md:ml-auto md:text-right">
            <MaskText
              lines={t.dual.heading}
              className="font-playfair text-4xl md:text-6xl leading-[0.95] tracking-[-0.02em]"
            />
            <p className="mt-6 max-w-sm md:ml-auto text-nowi-muted text-sm md:text-base">{t.dual.subtitle}</p>
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row w-full border-t border-white/10 min-h-[640px]">
        {panels.map((p) => {
          const isHovered = hovered === p.key;
          const isDimmed = hovered && hovered !== p.key;
          return (
            <motion.a
              href={p.href}
              key={p.key}
              data-testid={`dual-panel-${p.key}`}
              onMouseEnter={() => setHovered(p.key)}
              onMouseLeave={() => setHovered(null)}
              animate={{ flexGrow: isHovered ? 1.4 : isDimmed ? 0.6 : 1 }}
              transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
              className="group relative flex-1 basis-1/2 overflow-hidden border-b lg:border-b-0 lg:border-r border-white/10 last:border-r-0"
            >
              <motion.div
                className="absolute inset-0"
                animate={{ scale: isHovered ? 1.06 : 1, opacity: isDimmed ? 0.25 : 0.4 }}
                transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
              >
                <img src={p.img} alt={p.data.title} className="w-full h-full object-cover grayscale" />
              </motion.div>
              <div className="absolute inset-0 bg-gradient-to-t from-nowi-bg via-nowi-bg/70 to-transparent" />

              <div className="relative z-10 h-full flex flex-col justify-between p-8 md:p-12 min-h-[520px]">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[11px] uppercase tracking-[0.25em]" style={{ color: p.accent }}>
                    {p.data.kicker}
                  </span>
                  <p.Icon className="w-6 h-6" strokeWidth={1.25} style={{ color: p.accent }} />
                </div>

                <div>
                  <h3 className="font-playfair text-5xl md:text-7xl tracking-[-0.02em] mb-5">{p.data.title}</h3>
                  <p className="max-w-md text-nowi-muted text-sm md:text-base mb-6">{p.data.desc}</p>

                  <div className="flex flex-wrap gap-2 mb-8">
                    {p.data.tags.map((tag) => (
                      <span key={tag} className="font-mono text-[10px] uppercase tracking-[0.15em] border border-white/15 px-3 py-1.5 text-nowi-muted">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <span
                    className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-nowi-text"
                    style={{ borderBottom: `1px solid ${p.accent}` }}
                  >
                    {p.data.cta}
                    <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" strokeWidth={1.5} />
                  </span>
                </div>
              </div>
            </motion.a>
          );
        })}
      </div>
    </section>
  );
};
